/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect, useMemo } from 'react';
import { matchPath } from "react-router-dom";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { List, ListItem, ListItemIcon, ListItemText, Collapse, Typography, Divider } from "@material-ui/core";
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { NavLink } from 'react-router-dom';
import { useContext } from "react";
import { __RouterContext } from "react-router";
import { NavigationListItem } from "./components";
import { filterNavigationByRole, filterNavigationByFeatureFlags } from "utils/navigation-filter";
import { ADD_TIMESHEET_ROUTE, ADD_ADMIN_TIMESHEET_ROUTE, LIST_TIMESHEETS_ROUTE } from "features/timesheet/timesheet-routes";
import { ADD_JOB_PATH, JOBS_PATH } from "features/jobs";

const useRouter = () => useContext(__RouterContext);

// Material-UI's ListItem/ButtonBase expects the `component` to support refs.
// React Router's NavLink is a function component (no ref) unless wrapped.
const NavLinkRef = React.forwardRef((props, ref) => (
    <NavLink innerRef={ref} {...props} />
));

const useStyles = makeStyles((theme) => ({
	root: {
		marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative'
  },
  navContent: {
    flex: 1,
    overflowY: 'auto',
    position: 'relative',
    zIndex: 1,
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(0, 0, 0, 0.05)",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0, 0, 0, 0.2)",
      borderRadius: "3px",
      transition: "all 0.2s ease",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "rgba(0, 0, 0, 0.4)",
    },
    "&::-webkit-scrollbar-thumb:active": {
      background: "rgba(0, 0, 0, 0.6)",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(0, 0, 0, 0.4) rgba(0, 0, 0, 0.1)",
	},
	navigationTitle: {
		color: theme.palette.text.primary, // Use theme text color instead of white
	},
  item: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 2),
    margin: theme.spacing(0.5, 0),
    backgroundColor: theme.palette.highlight, // Use highlight color like HomeCard
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.highlight}`, // Subtle border
    width: '100%',
    boxShadow: theme.shadows[1], // Add subtle shadow like other cards
    // Slightly narrower on extra-small screens for more side spacing
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.25, 2),
      margin: theme.spacing(0.5, 3),
      width: `calc(100% - ${theme.spacing(6)}px)`,
    },
    '& .MuiListItem-button': {
      borderRadius: theme.spacing(1),
      backgroundColor: 'transparent', // Transparent to show parent background
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)', // Subtle hover effect
      }
    },
    '& .MuiListItemText-root': {
      '& .MuiListItemText-primary': {
        color: theme.palette.primary.main, // Dark blue text like HomeCard
        fontWeight: 'bold',
        letterSpacing: 0.1,
      },
      // Remove mobile-specific font overrides to match web
    },
    '&:hover': {
      backgroundColor: theme.palette.highlight,
      boxShadow: theme.shadows[2], // Enhanced shadow on hover
    },
    '&.disabled': {
      opacity: 0.5,
      pointerEvents: 'none',
      cursor: 'not-allowed',
      '& .MuiListItemText-root': {
        '& .MuiListItemText-primary': {
          color: theme.palette.text.disabled,
        },
      },
      '& .MuiListItemIcon-root': {
        color: theme.palette.text.disabled,
        '& svg': {
          color: theme.palette.text.disabled,
        },
      },
      '& .MuiSvgIcon-root': {
        color: theme.palette.text.disabled,
      },
    }
  },
  itemLeaf: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    margin: theme.spacing(0.75, 1), // Increased from 0.5 to 0.75 for even more spacing
    backgroundColor: theme.palette.highlight, // Match main item background
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.highlight}`,
    width: `calc(100% - ${theme.spacing(2)}px)`,
    boxShadow: theme.shadows[1],
    // Slightly narrower on extra-small screens for more side spacing
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2),
      margin: theme.spacing(0.75, 4),
      width: `calc(100% - ${theme.spacing(8)}px)`,
    },
    '& .MuiListItem-button': {
      borderRadius: theme.spacing(1),
      backgroundColor: 'transparent',
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)', // Subtle hover like main
      },
      '&.active': {
        backgroundColor: 'transparent',
      }
    },
    '& .MuiListItemText-root': {
      '& .MuiListItemText-primary': {
        color: theme.palette.primary.main, // Match main item text
        fontWeight: 'bold',
        letterSpacing: 0.1,
      },
      // Remove mobile-specific font overrides to match web
    },
    '&:hover': {
      backgroundColor: theme.palette.highlight,
      boxShadow: theme.shadows[2],
      borderColor: theme.palette.highlight,
    },
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
    cursor: 'not-allowed',
    '& .MuiListItemText-root': {
      '& .MuiListItemText-primary': {
        color: theme.palette.text.disabled,
      },
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.text.disabled,
      '& svg': {
        color: theme.palette.text.disabled,
      },
    },
  },
  button: {
    color: theme.palette.primary.main,
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      padding: '14px 8px',
    },
    // Remove mobile-specific font overrides to match web
    '&:hover': {
      backgroundColor: 'white',
      color: theme.palette.primary.main
    }
  },
  buttonLeaf: {
    color: theme.palette.text.primary,
    padding: '8px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    [theme.breakpoints.down('xs')]: {
      padding: '12px 8px',
    },
    '&.depth-0': {
      fontWeight: theme.typography.fontWeightMedium
    },
    // Remove mobile-specific font overrides to match web
    '&:hover': {
      backgroundColor: 'white',
      color: theme.palette.primary.main
    }
  },
  icon: {
    // Make the icon container a perfect circle
    borderRadius: '50%',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    width: 26,
    height: 26,
    minWidth: 26, // Smaller circle to better match text size
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: theme.spacing(1),
    '& svg': {
      fontSize: '1.1rem !important',
      width: '1.1rem !important',
      height: '1.1rem !important'
    }
  },
  expandIcon: {
    marginLeft: 'auto',
    height: 20,
    width: 20,
    color: theme.palette.primary.main, // Dark blue color to match text
    backgroundColor: theme.palette.background.paper, // White background
    borderRadius: '50%', // Circular background
    padding: 2, // Small padding
  },
  collapse: {
    width: '100%',
    padding: 0,
    margin: 0
  },

}));

/**
 * Helper function to determine if a menu item is available offline
 * @param {string} href - The href/path of the menu item
 * @returns {boolean} - True if the item is available offline
 */
const isMenuItemAvailableOffline = (href) => {
  if (!href) return false;
  
  // Items that work offline
  const offlineRoutes = [
    ADD_TIMESHEET_ROUTE,
    ADD_JOB_PATH,
    LIST_TIMESHEETS_ROUTE,
    JOBS_PATH,
  ];
  
  return offlineRoutes.some(route => {
    // Handle routes with query params or site IDs
    return href === route || href.startsWith(route + '/') || href.startsWith(route + '?');
  });
};

/**
 * Helper function to check if all children of a parent menu item are disabled when offline
 * @param {Array} children - Array of child menu items
 * @param {boolean} hasNetwork - Whether the app has network connectivity
 * @returns {boolean} - True if all children are disabled when offline
 */
const areAllChildrenDisabled = (children, hasNetwork) => {
  if (!children || children.length === 0 || hasNetwork) {
    return false;
  }
  
  return children.every(child => {
    const isAvailableOffline = isMenuItemAvailableOffline(child.href);
    return !isAvailableOffline;
  });
};

const NavigationList = (props) => {
	const { pages, ...rest } = props;

	return (
		<List>
			{pages.reduce(
				(items, page, index) =>
					reduceChildRoutes({ index, items, page, ...rest }),
				[]
			)}
		</List>
	);
};

NavigationList.propTypes = {
	depth: PropTypes.number,
	pages: PropTypes.array,
};

const reduceChildRoutes = (props) => {
	const { index, router, items, page, depth } = props;

	if (page.children) {
		const open = matchPath(router.location.pathname, {
			path: page.href,
			exact: false,
		});

		items.push(
			<NavigationListItem
				depth={depth}
				icon={page.icon}
				key={index}
				href={page.href}
				label={page.label}
				open={Boolean(open)}
				title={page.title}
			>
				<NavigationList
					depth={depth + 1}
					pages={page.children}
					router={router}
				/>
			</NavigationListItem>
		);
	} else {
		items.push(
			<NavigationListItem
				depth={depth}
				href={page.href}
				icon={page.icon}
				key={index}
				label={page.label}
				title={page.title}
			/>
		);
	}

	return items;
};

const Navigation = (props) => {
  const { 
    title, 
    pages, 
    className, 
    component: Component, 
    accountType, 
    organisations,
    currentOrganisation,
    role,
    timesheetToggle,
    hasNetwork = true,
    featureFlags,
    openAdminByDefault,
    supervisingStaffList,
    ...rest 
  } = props;

	const classes = useStyles(props);
	const router = useRouter();
  const [openSections, setOpenSections] = useState({
    'Home': !openAdminByDefault,
    'Activity': false,
    'Info': false,
    'Admin': !!openAdminByDefault
  });

  // Reset open sections when role changes to ensure menu updates properly
  useEffect(() => {
    setOpenSections({
      'Home': !openAdminByDefault,
      'Activity': false,
      'Info': false,
      'Admin': !!openAdminByDefault
    });
  }, [role, openAdminByDefault]);

  const handleToggleSection = (sectionName) => {
    setOpenSections(prev => {
      // If the section is currently open, close it
      if (prev[sectionName]) {
        return {
          ...prev,
          [sectionName]: false
        };
      }
      // If the section is closed, open it and close all others (accordion behavior)
      return {
        'Home': false,
        'Activity': false,
        'Info': false,
        'Admin': false,
        [sectionName]: true
      };
    });
  };

  // Filter pages based on user role - use useMemo to recalculate when role or featureFlags change
  const userRole = role || accountType;
  const roleFilteredPages = useMemo(
    () => filterNavigationByRole(pages, userRole, { supervisingStaffList }),
    [pages, userRole, supervisingStaffList]
  );
  // Then filter by feature flags
  const filteredPages = useMemo(() => filterNavigationByFeatureFlags(roleFilteredPages, featureFlags), [roleFilteredPages, featureFlags]);

  // Group pages by their main sections - use useMemo to recalculate when filteredPages change
  const groupedPages = useMemo(() => {
    const grouped = {
      'Home': [],
      'Activity': [],
      'Info': [],
      'Admin': []
    };

    if (filteredPages && filteredPages.length > 0) {
      filteredPages.forEach(page => {
        if (page.title === 'Home') {
          grouped['Home'].push(page);
        } else if (page.title === 'Info') {
          grouped['Info'].push(page);
        } else if (page.title === 'Activity') {
          grouped['Activity'].push(page);
        } else if (page.title === 'Admin') {
          grouped['Admin'].push(page);
        }
      });
    }
    
    return grouped;
  }, [filteredPages]);

	return (
		<Component {...rest} className={clsx(classes.root, className)}>
      <div className={classes.navContent}>
        <List>
          {/* Home section */}
          {groupedPages['Home'].map(item => (
            <div key={item.title}>
              <ListItem
                className={classes.item}
                disableGutters
                onClick={() => handleToggleSection('Home')}
                button
              >
                {item.icon && (
                  <ListItemIcon className={classes.icon}>
                    <item.icon />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{ color: 'inherit' }}
                />
                {openSections['Home'] ? (
                  <ExpandLess className={classes.expandIcon} />
                ) : (
                  <ExpandMore className={classes.expandIcon} />
                )}
              </ListItem>
              <Collapse in={openSections['Home']} timeout="auto" unmountOnExit>
                <List>
                  {item.children && item.children.map(child => {
                    // Conditionally change the title for the timesheet menu item
                    let displayTitle = child.title;
                    if (child.title === "+ Timesheet" && timesheetToggle) {
                      displayTitle = "View Active Timesheet";
                    }
                    
                    // "+ Timesheet" always uses the staff timer flow; Timer feature flag has been removed.
                    let href = child.href;
                    if (child.title === "+ Timesheet") {
                      href = ADD_TIMESHEET_ROUTE;
                    }
                    
                    // Check if item should be disabled when offline
                    const isAvailableOffline = isMenuItemAvailableOffline(href);
                    const isDisabled = !hasNetwork && !isAvailableOffline;
                    
                    return (
                      <ListItem
                        key={child.title}
                        className={clsx(classes.itemLeaf, { [classes.disabled]: isDisabled })}
                        disableGutters
                        button={!isDisabled}
                        component={isDisabled ? 'div' : NavLinkRef}
                        exact
                        to={href}
                        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                      >
                        {child.icon && (
                          <ListItemIcon className={classes.icon}>
                            <child.icon />
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={displayTitle}
                          primaryTypographyProps={{ color: 'inherit' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </div>
          ))}

          {/* Activity section */}
          {groupedPages['Activity'].map(item => (
            <div key={item.title}>
              <ListItem
                className={classes.item}
                disableGutters
                onClick={() => handleToggleSection('Activity')}
                button
              >
                {item.icon && (
                  <ListItemIcon className={classes.icon}>
                    <item.icon />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{ color: 'inherit' }}
                />
                {openSections['Activity'] ? (
                  <ExpandLess className={classes.expandIcon} />
                ) : (
                  <ExpandMore className={classes.expandIcon} />
                )}
              </ListItem>
              <Collapse in={openSections['Activity']} timeout="auto" unmountOnExit>
                <List>
                  {item.children && item.children.map(child => {
                    // Check if item should be disabled when offline
                    const isAvailableOffline = isMenuItemAvailableOffline(child.href);
                    const isDisabled = !hasNetwork && !isAvailableOffline;
                    
                    return (
                      <ListItem
                        key={child.title}
                        className={clsx(classes.itemLeaf, { [classes.disabled]: isDisabled })}
                        disableGutters
                        button={!isDisabled}
                        component={isDisabled ? 'div' : NavLinkRef}
                        exact
                        to={child.href}
                        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                      >
                        {child.icon && (
                          <ListItemIcon className={classes.icon}>
                            <child.icon />
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={child.title}
                          primaryTypographyProps={{ color: 'inherit' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </div>
          ))}

          {/* Info section */}
          {groupedPages['Info'].map(item => {
            const allChildrenDisabled = areAllChildrenDisabled(item.children, hasNetwork);
            
            return (
              <div key={item.title}>
                <ListItem
                  className={clsx(classes.item, { [classes.disabled]: allChildrenDisabled })}
                  disableGutters
                  onClick={allChildrenDisabled ? undefined : () => handleToggleSection('Info')}
                  button={!allChildrenDisabled}
                  component={allChildrenDisabled ? 'div' : 'button'}
                >
                  {item.icon && (
                    <ListItemIcon className={classes.icon}>
                      <item.icon />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ color: 'inherit' }}
                  />
                  {openSections['Info'] ? (
                    <ExpandLess className={classes.expandIcon} />
                  ) : (
                    <ExpandMore className={classes.expandIcon} />
                  )}
                </ListItem>
              <Collapse in={openSections['Info']} timeout="auto" unmountOnExit>
                <List>
                  {item.children && item.children.map(child => {
                    // Check if item should be disabled when offline
                    const isAvailableOffline = isMenuItemAvailableOffline(child.href);
                    const isDisabled = !hasNetwork && !isAvailableOffline;
                    
                    return (
                      <ListItem
                        key={child.title}
                        className={clsx(classes.itemLeaf, { [classes.disabled]: isDisabled })}
                        disableGutters
                        button={!isDisabled}
                        component={isDisabled ? 'div' : NavLinkRef}
                        exact
                        to={child.href}
                        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                      >
                        {child.icon && (
                          <ListItemIcon className={classes.icon}>
                            <child.icon />
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={child.title}
                          primaryTypographyProps={{ color: 'inherit' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </div>
            );
          })}

          {/* Admin section */}
          {groupedPages['Admin'].map(item => {
            const allChildrenDisabled = areAllChildrenDisabled(item.children, hasNetwork);
            
            return (
              <div key={item.title}>
                <ListItem
                  className={clsx(classes.item, { [classes.disabled]: allChildrenDisabled })}
                  disableGutters
                  onClick={allChildrenDisabled ? undefined : () => handleToggleSection('Admin')}
                  button={!allChildrenDisabled}
                  component={allChildrenDisabled ? 'div' : 'button'}
                >
                  {item.icon && (
                    <ListItemIcon className={classes.icon}>
                      <item.icon />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ color: 'inherit' }}
                  />
                  {openSections['Admin'] ? (
                    <ExpandLess className={classes.expandIcon} />
                  ) : (
                    <ExpandMore className={classes.expandIcon} />
                  )}
                </ListItem>
              <Collapse in={openSections['Admin']} timeout="auto" unmountOnExit>
                <List>
                  {item.children && item.children.map(child => {
                    // Check if item should be disabled when offline
                    const isAvailableOffline = isMenuItemAvailableOffline(child.href);
                    const isDisabled = !hasNetwork && !isAvailableOffline;
                    
                    return (
                      <ListItem
                        key={child.title}
                        className={clsx(classes.itemLeaf, { [classes.disabled]: isDisabled })}
                        disableGutters
                        button={!isDisabled}
                        component={isDisabled ? 'div' : NavLinkRef}
                        exact
                        to={child.href}
                        onClick={isDisabled ? (e) => e.preventDefault() : undefined}
                      >
                        {child.icon && (
                          <ListItemIcon className={classes.icon}>
                            <child.icon />
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={child.title}
                          primaryTypographyProps={{ color: 'inherit' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </div>
            );
          })}
        </List>

      </div>
		</Component>
	);
};

Navigation.propTypes = {
	className: PropTypes.string,
	component: PropTypes.any,
	pages: PropTypes.array.isRequired,
	title: PropTypes.string,
  accountType: PropTypes.string,
  organisations: PropTypes.array,
  currentOrganisation: PropTypes.string,
  role: PropTypes.string,
  timesheetToggle: PropTypes.bool,
  hasNetwork: PropTypes.bool,
  openAdminByDefault: PropTypes.bool,
  supervisingStaffList: PropTypes.array,
};

Navigation.defaultProps = {
	component: "nav",
  accountType: "",
  organisations: [],
  currentOrganisation: ""
};

export default Navigation;